import { createContext, ReactNode, useMemo } from 'react';
import {
  createPath,
  Location,
  NavigateFunction,
  Path,
  To,
  useBlocker,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { useBasename } from 'shared/helpers/RoutingHelpers';
import { useNullableContext } from 'shared/hooks/useNullableContext';
import { PostingSearchCriteriaDTO } from 'shared/modules/loadboard/PostingSearchCriteriaDTO';
import { parseSearchQuery, updateSearchQuery } from 'shared/utils/URLUtils';
import { PostingDTO } from './dto/PostingDTO';
import { PostingSavedSearchDTO } from './dto/PostingSavedSearchDTO';
import {
  convertPostingPageParamsToQuery,
  LoadSearchSource,
  PostingsPageTab,
} from './dto/PostingsPageParamsDTO';
import { markSearchAsUserTriggered } from './LoadboardPostingAPI';
import { Action } from '@remix-run/router';

// In order to detect if history.goBack works, we will keep history count
// history.goBack will work if the counter is more than zero.
let historyCounter = 0;

function createPostingsPageRouter(
  navigate: NavigateFunction,
  location: Location,
  basename: string,
) {
  const createAbsolutePath = (nextLocation: Partial<Path>) => {
    const path = createPath(nextLocation);
    return basename ? `/${basename}${path}` : path;
  };

  const tryGoBack = (fallbackLocation: Path | To, replace = true) => {
    const { back_url } = parseSearchQuery(location.search);

    if (back_url) {
      navigate(back_url, { replace });
      return;
    }

    if (historyCounter > 0) {
      navigate(-1);
      return;
    }

    navigate(fallbackLocation, { replace });
  };

  return {
    tryGoBack,
    openPostingScreen(
      posting: PostingDTO,
      strategy: 'replace' | 'push' | 'blank' = 'push',
      source?:
        | string
        | {
            utm_medium?: string;
            utm_source?: string;
            utm_content?: string;
          },
    ) {
      const utmFields =
        typeof source === 'string' ? { utm_medium: source } : (source ?? {});

      const nextLocation = {
        pathname: '/loads',
        search: updateSearchQuery(location.search, (prev) => {
          let loadGuid = posting.load.guid;
          let tab = prev.current_tab;
          // current_tab value is being parsed from url to identify which guid will be inserted to params as current_load_guid
          if (tab === 'booked' || tab === 'requested') {
            if (posting.carrier_load?.guid) {
              loadGuid = posting.carrier_load.guid;
            } else {
              tab = 'available';
            }
          }

          return {
            ...prev,
            utm_medium: utmFields.utm_medium ?? prev.utm_medium,
            utm_source: utmFields.utm_source ?? prev.utm_source,
            utm_content: utmFields.utm_content ?? prev.utm_content,
            current_load_guid: loadGuid,
            current_tab: tab,
          };
        }),
      };

      if (strategy === 'replace') {
        navigate(nextLocation, { replace: true });
      } else if (strategy === 'push') {
        navigate(nextLocation);
      } else {
        window.open(createAbsolutePath(nextLocation), '_blank');
      }
    },
    closePostingScreen() {
      tryGoBack({
        search: updateSearchQuery(location.search, (prev) => ({
          ...prev,
          utm_medium: undefined,
          utm_source: undefined,
          utm_content: undefined,
          current_load_guid: undefined,
        })),
      });
    },

    openSearchPostingsScreen({
      tab,
      source,
      searchCriteria,
      strategy = 'push',
    }: {
      tab?: PostingsPageTab;
      source?: LoadSearchSource;
      searchCriteria: Partial<PostingSearchCriteriaDTO>;
      strategy?: 'replace' | 'push' | 'blank';
    }) {
      const nextLocation = {
        pathname: '/loads',
        search: updateSearchQuery(location.search, (prev) => ({
          ...prev,
          ...convertPostingPageParamsToQuery({
            current_page: 0,
            current_tab: tab || prev.current_tab,
            search_criteria: searchCriteria,
            ...(source ? { utm_medium: source } : undefined),
          }),
        })),
      };

      if (strategy === 'replace') {
        navigate(nextLocation, { replace: true });
      } else if (strategy === 'push') {
        navigate(nextLocation);
      } else {
        window.open(createAbsolutePath(nextLocation), '_blank');
      }
    },

    openPostingsScreen(
      tab: PostingsPageTab,
      replace = false,
      source?: { utm_medium: 'Suggested Load'; utm_content: 'View All' },
    ) {
      const nextLocation: To = {
        pathname: '/loads',
        search: updateSearchQuery(location.search, (prev) => {
          if (
            prev.current_tab === 'private-loadboard' ||
            prev.current_saved_search_guid
          ) {
            return {
              ...prev,
              ...convertPostingPageParamsToQuery({ current_tab: tab }),
              ...source,
            };
          }
          return {
            ...prev,
            ...convertPostingPageParamsToQuery({
              current_tab: tab,
              search_criteria: prev.search_criteria,
            }),
            ...source,
          };
        }),
      };
      navigate(nextLocation, { replace });
    },

    openSavedSearchScreen({
      source,
      savedSearch,
      replace = false,
    }: {
      backUrl?: string;
      replace?: boolean;
      source?: LoadSearchSource;
      savedSearch: PostingSavedSearchDTO;
    }) {
      const nextLocation: To = {
        pathname: '/loads',
        search: updateSearchQuery(location.search, (prev) => ({
          ...prev,
          ...convertPostingPageParamsToQuery({
            current_page: 0,
            search_criteria: markSearchAsUserTriggered(
              savedSearch.search_criteria,
            ),
            current_saved_search_guid: savedSearch.guid,
            ...(source ? { utm_medium: source } : {}),
          }),
        })),
      };

      navigate(nextLocation, { replace });
    },
  };
}

interface LoadboardContext {
  router: ReturnType<typeof createPostingsPageRouter>;
}

const Context = createContext<null | LoadboardContext>(null);

export function useLoadboardContext() {
  return useNullableContext('Loadboard', Context);
}

interface LoadboardContextProviderProps {
  children: ReactNode;
}

export function LoadboardContextProvider({
  children,
}: LoadboardContextProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const basename = useBasename();

  useBlocker(({ historyAction }) => {
    if (historyAction === Action.Push) {
      historyCounter++;
    }

    if (historyAction === Action.Pop) {
      historyCounter--;
    }

    return false;
  });

  const loadboardRouter = useMemo(
    () => createPostingsPageRouter(navigate, location, basename),
    [navigate, location, basename],
  );

  const ctx = useMemo(
    (): LoadboardContext => ({ router: loadboardRouter }),
    [loadboardRouter],
  );

  return <Context.Provider value={ctx}>{children}</Context.Provider>;
}
